import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable } from 'rxjs';
import { NgClass } from '@angular/common';
import {
  SituationCategoriesResponse,
  ResponseCategoriesResponse,
} from '../../../../shared/models/category.models';
import { ModalComponent } from '../../../../shared/components/modal/modal';
import { SelectComponent, SelectOption } from '../../../../shared/components/select/select';
import { CategoryService } from '../../../../core/services/category.service';
import { TaxonomyMemberService } from '../../../../core/services/taxonomy-member.service';
import { ToastService } from '../../../../core/services/toast.service';
import { apiErrorMessage } from '../../../../shared/utils/api-error';

@Component({
  selector: 'app-add-taxonomy-category-modal',
  imports: [ModalComponent, NgClass, SelectComponent],
  templateUrl: './add-taxonomy-category-modal.html',
})
export class AddTaxonomyCategoryModalComponent implements OnInit {
  taxonomyId = input.required<string>();
  type = input.required<'situation' | 'response'>();
  totalMembers = input<number>(0);
  closed = output<void>();
  saved = output<void>();

  private categoryService = inject(CategoryService);
  private taxonomyMemberService = inject(TaxonomyMemberService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  protected categoryOptions = signal<SelectOption[]>([]);
  protected selectedCategoryId = signal('');
  protected definition = signal('');
  protected active = signal(true);
  protected showErrors = signal(false);
  protected submitting = signal(false);
  protected loading = signal(true);

  protected selectedCategoryLabel = computed(
    () => this.categoryOptions().find((o) => o.value === this.selectedCategoryId())?.label ?? '',
  );

  ngOnInit(): void {
    const isSituation = this.type() === 'situation';
    const id = this.taxonomyId();

    const categories$: Observable<SituationCategoriesResponse | ResponseCategoriesResponse> =
      isSituation
        ? this.categoryService.getSituationCategories()
        : this.categoryService.getResponseCategories();

    const pageSize = Math.max(this.totalMembers(), 1000);
    const members$ = isSituation
      ? this.taxonomyMemberService.getSituationTaxonomyMembers(id, { pageSize, pageIndex: 0 })
      : this.taxonomyMemberService.getResponseTaxonomyMembers(id, { pageSize, pageIndex: 0 });

    forkJoin({ categories: categories$, members: members$ })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ categories, members }) => {
          const list =
            'situationCategories' in categories
              ? categories.situationCategories
              : categories.responseCategories;

          const membersAny = members as any;
          const memberList =
            membersAny.situationTaxonomiesMembers ?? membersAny.responseTaxonomiesMembers ?? [];
          const excluded = new Set<string>(
            memberList.map((m: any) => m.situationCategoryId ?? m.responseCategoryId),
          );

          this.categoryOptions.set(
            list.filter((c) => !excluded.has(c.id)).map((c) => ({ value: c.id, label: c.name })),
          );
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected onSubmit(): void {
    this.showErrors.set(true);
    if (!this.selectedCategoryId()) return;

    this.submitting.set(true);
    const request$: Observable<unknown> =
      this.type() === 'situation'
        ? this.taxonomyMemberService.createSituationTaxonomyMember({
            situationCategoryId: this.selectedCategoryId(),
            situationTaxonomyId: this.taxonomyId(),
            active: this.active(),
            definition: this.definition().trim(),
            followUpRequired: 0,
            situationCategory: {
              id: this.selectedCategoryId(),
              name: this.selectedCategoryLabel(),
            },
          })
        : this.taxonomyMemberService.createResponseTaxonomyMember({
            responseCategoryId: this.selectedCategoryId(),
            responseTaxonomyId: this.taxonomyId(),
            active: this.active(),
            definition: this.definition().trim(),
          });

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.toastService.show('Category added successfully');
        this.saved.emit();
      },
      error: (err) => {
        this.toastService.show(apiErrorMessage(err, 'Failed to add category'), 'error');
        this.submitting.set(false);
      },
    });
  }
}
